/**
 * 렌더가 DOM 에 싣는 `data-*` 속성 이름 모음.
 *
 * 이 상수의 **보호 범위는 쓰는 쪽뿐**이다. 읽는 쪽은 CSS selector·`getAttribute`·
 * `querySelector` 에서 생 문자열을 쓰는 곳이 많고(특히 `cellId`·`cellIdentityKey`),
 * CSS·Tailwind 는 애초에 TS 상수를 못 읽는다. **값을 바꾸면 컴파일은 통과한 채
 * 그 조회들이 런타임에 조용히 깨진다** — 값 변경 전에 반드시 리터럴을 함께 grep 할 것.
 *
 * 그래서 항목마다 **쓰기/읽기 지점**을 적어 둔다. 필드가 기능별로 갈리지 않고
 * (`cellId` 는 ixd-editor·dsd-editor·guideline-check·workbook-match 가 함께 쓴다)
 * 네임스페이스로 나눌 축이 없어, 소속 대신 소비처를 적는 쪽을 택했다. 새 항목도
 * 같은 형식으로 쓴다 — 읽는 곳이 아직 없으면 "없음" 과 그 이유를 적는다.
 *
 * 컴포넌트(렌더)와 util(cell-display 등) 양쪽에서 import 하므로 의존 방향이 한쪽으로
 * 흐르도록 중립적인 utils 에 둔다.
 */
export const ATTR = {
  /**
   * 목차 섹션 래퍼 식별자.
   * - 쓰기: `document-sidebar`, `preview-diff-section`(리터럴)
   * - 읽기: `ixd-preview-compare/result-view`(리터럴 `section[data-section-id=…]`)
   */
  sectionId: "data-section-id",
  /**
   * 셀 식별자(IXD 는 cellId, DSD 는 dsdId).
   * - 쓰기: `ixd-editor/cell-display`, `ixd-dsd-compare/table-cell`,
   *   `dsd-preview-renderer`
   * - 읽기: **20곳 이상이 리터럴 `[data-cell-id]`** 로 조회한다(header-click-targets,
   *   header-role, use-ixd-cell-review-styles, guideline-check/result-view …).
   *   이 파일에서 가장 넓게 쓰이고 가장 덜 보호되는 이름이다.
   */
  cellId: "data-cell-id",
  /**
   * 정규화 전 원본 cell_id. 렌더 id 와 다를 수 있어(문장영역 태그의 period 채움 등)
   * 변환 전 id 로도 셀을 찾을 수 있게 함께 싣는다.
   * - 쓰기: `ixd-editor/cell-display`
   * - 읽기: `ixd-dsd-compare/cell-id-selector`, `use-ixd-same-value-popover`,
   *   `use-ixd-cell-edit-handlers`
   */
  sourceCellId: "data-source-cell-id",
  /**
   * 같은 값 연동 그룹 키 — 한 cellId 가 여러 물리 셀로 렌더될 때 올바른 셀만 고른다.
   * - 쓰기: `ixd-editor/cell-display`
   * - 읽기: `use-ixd-cell-edit-handlers`, `modified-cell-values`(둘 다 ATTR),
   *   `use-ixd-same-value-popover`, `use-table-cell-selection`,
   *   `use-ixd-cell-review-styles`(셋 다 리터럴)
   */
  cellIdentityKey: "data-cell-identity-key",
  /**
   * DSD 셀 → 매칭 IXD cellId 목록(개행 구분).
   * - 쓰기: `dsd-preview-renderer`(문장 인라인 매칭 세그먼트),
   *   `preview-diff-table`(리터럴)
   * - 읽기: **저장소 안에 없음.** 정적 HTML 다운로드본에서 외부/수동 소비용이다.
   */
  pairCellId: "data-pair-cell-id",
  /**
   * DSD 셀 → 대표(가장 적절한) 매칭 IXD cellId.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: 없음 — `pairCellId` 와 같은 이유(다운로드본용)
   */
  primaryCellId: "data-primary-cell-id",
  /**
   * DSD 셀이 배정된 DL view id. 근거로 배정된 셀에만 붙는다 — **속성 부재가
   * "DL 미배정"** 이고, 빈 문자열로 표현하지 않는다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 `body[data-region-review]` 규칙(리터럴) + 다운로드본·
   *   DevTools. 클릭 시 넘기는 dlViewId 는 DOM 이 아니라 렌더러의 Map 에서 나온다.
   */
  dlViewId: "data-dl-view-id",
  /**
   * DSD 셀이 배정된 기간(`CURRENT` | `PREVIOUS` | `BEFORE_PREVIOUS`).
   * `dlViewId` 와 같은 규약 — 속성 부재가 "기간 미배정"이다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `dlViewId` 와 같다.
   */
  period: "data-period",
  /**
   * DSD 셀이 배정된 line_item 태그 id. `dlViewId` 와 같은 규약 — **속성 부재가
   * "항목 미배정"** 이다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 `body[data-region-review="line-item"]` 규칙(리터럴)
   *   + 다운로드본·DevTools.
   */
  lineItemId: "data-line-item-id",
  /**
   * DSD 셀이 배정된 항목의 표 안 순번(색 배정용). `dlIndex` 와 같은 이유 —
   * `lineItemId` 는 임의 태그 id 라 CSS 로 항목별 색을 못 고른다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 `body[data-region-review="line-item"]` 규칙(리터럴).
   *   색 개수는 `review-style-tokens` 의 `LINE_ITEM_REVIEW_COLOR_COUNT` 와 맞춘다.
   */
  lineItemIndex: "data-line-item-index",
  /**
   * DSD 표 안에서 그 line_item 이 **처음 나오는 셀 하나**에만 붙는 한글 이름표.
   * `dlTitle` 과 같은 규약 — 배정 자체는 `lineItemId` 가 모든 셀에 싣고, 라벨을
   * 못 찾으면 안 붙는다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 배정 검토 규칙(리터럴)
   */
  lineItemTitle: "data-line-item-title",
  /**
   * DSD 셀이 배정된 DL 의 chunk 안 순번(색 배정용). 값 자체엔 의미가 없다 —
   * `dlViewId` 는 임의 문자열이라 CSS 로 영역별 색을 못 고르기 때문에 둔다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 `body[data-region-review]` 규칙(리터럴).
   *   색 개수는 `review-style-tokens` 의 `DL_REVIEW_COLOR_COUNT` 와 맞춘다.
   */
  dlIndex: "data-dl-index",
  /**
   * DSD 표 안에서 그 DL 이 **처음 나오는 셀 하나**에만 붙는 한글 제목(영역 이름표).
   * 배정 자체는 `dlViewId` 가 모든 셀에 싣는다 — 이름을 셀마다 반복하면 표를 덮는다.
   * 제목을 못 찾은 DL 은 안 붙는다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 `body[data-region-review]` 규칙(리터럴)
   */
  dlTitle: "data-dl-title",
  /**
   * DSD 셀이 **검토 중인 축**에서 배정된 member 태그 id. `lineItemId` 와 같은 규약 —
   * 속성 부재가 "그 축에서 미배정" 이다. 한 셀은 축마다 member 를 하나씩 갖지만 면색·
   * 이름표 채널이 하나뿐이라, 한 번에 한 축만 싣는다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css` 의 `body[data-region-review="member"]` 규칙(리터럴)
   */
  memberId: "data-member-id",
  /**
   * 그 member 의 표 안 순번(색 배정용). `lineItemIndex` 와 같은 이유.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css`(리터럴). 색 개수는 `LINE_ITEM_REVIEW_COLOR_COUNT` 공유.
   */
  memberIndex: "data-member-index",
  /**
   * 그 member 가 **처음 나오는 셀 하나**에만 붙는 한글 이름표. `lineItemTitle` 과 같은 규약.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `styles/global.css`(리터럴)
   */
  memberTitle: "data-member-title",
  /**
   * 라인아이템 라벨.
   * - 쓰기: `ixd-dsd-compare/table-cell`
   * - 읽기: `match-navigation-popover`(리터럴) — 네비 팝오버가 DOM 에서만 읽으므로
   *   이 속성이 빠지면 항목 라벨이 값으로 폴백한다.
   */
  lineItemLabel: "data-line-item-label",
  /**
   * 사이드바 접기/펼치기 토글 버튼 표시.
   * - 쓰기: `document-sidebar`
   * - 읽기: **없음.** 같은 엘리먼트의 `data-sidebar-document-toggle`(리터럴)도
   *   읽는 곳이 없다. 소비처가 끝내 안 생기면 둘 다 지울 후보.
   */
  sidebarToggle: "data-sidebar-toggle",
  /**
   * 축 합계(도메인) 값 셀 표시. 값 유무가 아니라 **존재 여부**로 판정한다.
   * - 쓰기: `ixd-editor/cell-display`, `table-preview/render-table`
   * - 읽기: `ixd-workbook-match/paste-match-button`(합계 열/행 짝짓기)
   */
  axisTotal: "data-axis-total",
  /**
   * DSD 표 블록 래퍼 — 포커싱의 "표" 단위(표 경계 전용).
   * 가로 스크롤 노드가 아니다 — 실제 스크롤 노드는 래퍼 안 TableScrollArea 의
   * Radix Viewport 다. 이 요소에 `scrollLeft` 를 써도 아무 일도 일어나지 않는다.
   * - 쓰기: `dsd-preview-renderer`
   * - 읽기: `ixd-dsd-compare/focus-utils`(`DSD_SECTION_SELECTOR`),
   *   `styles/global.css` 의 배정 검토 규칙(리터럴)
   */
  tableBlock: "data-table-block",
} as const;

/**
 * 배정 검토 모드 스위치 — `ATTR` 과 달리 셀이 아니라 `body` 에 붙는다.
 * - 쓰기: `region-review-toggle`
 * - 읽기: `styles/global.css` 의 `body[data-region-review="…"]` 규칙(리터럴)
 */
export const REGION_REVIEW_ATTR = "data-region-review";

/** `REGION_REVIEW_ATTR` 이 가질 수 있는 값 — global.css 의 규칙 집합과 짝이다. */
export type RegionReviewMode =
  | "off"
  | "dl"
  | "period"
  | "line-item"
  | "member"
  | "all";
